/**
 * @fileoverview Componente de card de promoção para visualização de usuários.
 * Exibe imagem, nome e descrição da promoção.
 * Admin: visualiza status da promoção (ativa/inativa) com chip.
 * 
 * @module PromotionCard
 * @requires react
 * @requires @mui/material
 * @since 1.0.0
 */

import { memo } from 'react';
import {
  Card,
  Box,
  useTheme,
  CardMedia,
  Chip
} from '@mui/material';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';

/**
 * Componente de card de promoção.
 * Exibe apenas a imagem da promoção ocupando todo o card.
 * Sem imagem: exibe ícone centralizado.
 * Admin: chip de status no canto superior direito.
 *
 * @component
 * @param {Object} props
 * @param {string|number} props.id
 * @param {string} props.name
 * @param {string} [props.description]
 * @param {string} [props.image_url]
 * @param {string} [props.status='active']
 * @param {boolean} [props.isAdmin=false]
 * @param {Function} [props.onClick]
 * @returns {JSX.Element}
 */
function PromotionCard({ id, name, description, image_url, status = 'active', isAdmin = false, onClick }) {
  const theme = useTheme();
  const isActive = status === 'active';

  const handleCardClick = () => {
    if (onClick) onClick({ id, name, description, image_url, status });
  };

  return (
    <Card
      onClick={handleCardClick}
      sx={{
        position: 'relative',
        width: { xs: '98%', sm: '100%' },
        minWidth: { xs: 0, sm: 280 },
        maxWidth: '100%',
        height: 350,
        minHeight: 350,
        maxHeight: 350,
        flex: 1,
        opacity: isActive ? 1 : 0.6,
        cursor: 'pointer',
        overflow: 'hidden',
        '&:hover': {
          boxShadow: theme.shadows[4],
          transform: 'translateY(-2px)',
          transition: 'all 0.3s ease',
        },
      }}
    >
      {image_url ? (
        <CardMedia
          component="img"
          image={image_url}
          alt={name}
          sx={{ width: '100%', height: 175, objectFit: 'contain' }}
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
      ) : null}

      {/* Fallback sem imagem */}
      <Box
        sx={{
          display: image_url ? 'none' : 'flex',
          width: '100%',
          height: '100%',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.50',
        }}
      >
        <LocalOfferIcon sx={{ fontSize: 80, color: 'primary.main' }} />
      </Box>

      {/* Chip de status para admin */}
      {isAdmin && (
        <Chip
          label={isActive ? 'Ativa' : 'Inativa'}
          color={isActive ? 'success' : 'default'}
          size="small"
          sx={{ position: 'absolute', top: 8, right: 8 }}
        />
      )}
    </Card>
  );
}

export default memo(PromotionCard);
